/**
 * DemoPage — Página de entrada al demo de Conexo POS.
 * Ruta: /demo
 *
 * Flujo:
 *  1. Capture email (guard de lead)
 *  2. POST /api/demo/access → recibe token JWT demo
 *  3. Guarda token + demo_expires_at en sessionStorage
 *  4. Redirige a /admin (o DevSelector en localhost)
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000/api").replace(/\/api\/?$/, "");

export default function DemoPage() {
    const navigate = useNavigate();
    const [step, setStep] = useState("email"); // "email" | "loading" | "error"
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [error, setError] = useState("");

    const features = [
        "✅ TPV táctil completo (admin, caja, cocina)",
        "✅ Panel de pedidos en tiempo real",
        "✅ Gestión de mesas y QR (plan Enterprise)",
        "✅ Dashboard de métricas",
        "✅ Multi-sucursal",
        "⚠️  Impresión desactivada en demo",
        "⚠️  Sesión de 15 minutos",
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) { setError("Introduce tu email."); return; }
        setStep("loading");
        setError("");

        try {
            const res = await axios.post(`${API}/api/demo/session`, { email: email.trim(), name: name.trim() });
            const { access_token, demo } = res.data;

            // Guardar token y metadata de demo
            localStorage.setItem("session_token", access_token);
            sessionStorage.setItem("demo_mode", "true");
            sessionStorage.setItem("demo_expires_at", demo.expires_at);

            // Configurar modo demo local independiente del DNS
            sessionStorage.setItem("dev_mode", "tenant"); // Funciona para localhost
            sessionStorage.setItem("dev_subdomain", "demo");

            // Navegar directamente al POS como solicitado
            window.location.href = demo.redirect_url || "/admin/pos";
        } catch (err) {
            const msg = err.response?.data?.detail || "Error al acceder al demo. Inténtalo de nuevo.";
            setError(msg);
            setStep("email");
        }
    };

    return (
        <div style={s.root}>
            {/* Glow */}
            <div style={s.glow} />

            <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-center max-w-5xl w-full z-10 px-4">
                {/* Left */}
                <div className="flex-1 text-white text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-8">
                        <span className="text-[#0B3C5D] text-xl bg-yellow-500 rounded px-2 py-1">◆</span>
                        <span className="font-extrabold text-base text-white">Conexo POS</span>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-3">Prueba el sistema completo<br />antes de comprometerte.</h1>
                    <p className="text-slate-500 text-lg mb-8">Acceso inmediato. Sin tarjeta. Sin instalación.</p>

                    <ul className="list-none p-0 m-0 flex flex-col gap-3 text-left w-max mx-auto md:mx-0">
                        {features.map((f, i) => (
                            <li key={i} className="text-slate-400 text-sm md:text-base">{f}</li>
                        ))}
                    </ul>
                </div>

                {/* Right — Form */}
                <div style={s.card} className="w-full max-w-sm shrink-0">
                    <div className="text-center mb-7">
                        <div className="text-4xl mb-3">🚀</div>
                        <h2 className="text-white text-xl font-bold mb-1.5">Acceder al Demo</h2>
                        <p className="text-slate-400 text-sm m-0">Solo necesitamos tu email para empezar</p>
                    </div>

                    {step === "loading" ? (
                        <div className="flex flex-col items-center gap-4 py-8">
                            <div style={s.spinner} />
                            <p className="text-slate-400">Preparando tu demo...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-slate-400 text-xs text-left">Tu email</label>
                                <input
                                    id="demo-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                                    placeholder="hola@turestaurante.com"
                                    className="bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:ring-2 focus:ring-[#0B3C5D]"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-slate-400 text-xs text-left">Tu nombre (opcional)</label>
                                <input
                                    id="demo-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Paco García"
                                    className="bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:ring-2 focus:ring-[#0B3C5D]"
                                />
                            </div>

                            {error && <p className="text-red-400 text-xs m-0">{error}</p>}

                            <button id="demo-submit" type="submit" style={s.btn} className="mt-1 shadow-lg scale-100 hover:scale-105 transition-transform">
                                Entrar al Demo →
                            </button>

                            <p className="text-slate-600 text-xs text-center m-0 mt-2">
                                Al continuar aceptas que usemos tu email para mostrarte cómo funciona Conexo POS.
                                Sin spam.
                            </p>
                        </form>
                    )}

                    <div className="h-px bg-white/5 my-5" />

                    <p className="text-slate-500 text-xs text-center m-0">
                        ¿Listo para empezar?{" "}
                        <a id="demo-to-onboarding" href="/onboarding" className="text-yellow-500 no-underline font-bold">
                            Crea Conexo POS gratis →
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}

const s = {
    root: {
        minHeight: "100vh", background: "#0a0f1a",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Inter', system-ui, sans-serif", padding: "2rem", position: "relative",
        overflow: "hidden",
    },
    glow: {
        position: "absolute", top: "10%", left: "5%",
        width: "600px", height: "600px",
        background: "radial-gradient(circle, rgba(11,60,93,0.4) 0%, transparent 60%)",
        pointerEvents: "none",
    },
    wrapper: { display: "flex", gap: "4rem", alignItems: "center", maxWidth: "960px", width: "100%", zIndex: 1 },
    left: { flex: 1, color: "#fff" },
    logoBadge: { display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "2rem" },
    logoIcon: { color: "#0B3C5D", fontSize: "1.25rem", background: "#EAB308", borderRadius: "4px", padding: "0.2rem 0.4rem" },
    logoText: { fontWeight: "800", fontSize: "1rem", color: "#fff" },
    title: { fontSize: "2rem", fontWeight: "800", lineHeight: 1.2, margin: "0 0 0.75rem" },
    sub: { color: "#64748b", fontSize: "1rem", margin: "0 0 2rem" },
    featureList: { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.625rem" },
    featureItem: { color: "#94a3b8", fontSize: "0.875rem" },
    // Card
    card: {
        background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "1.5rem", padding: "2rem",
        boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
    },
    btn: {
        background: "linear-gradient(135deg, #0B3C5D, #1a6fa8)",
        border: "none", borderRadius: "0.75rem", padding: "0.875rem",
        color: "#fff", fontWeight: "700", fontSize: "1rem", cursor: "pointer",
        fontFamily: "inherit", marginTop: "0.25rem",
    },
    legal: { color: "#334155", fontSize: "0.72rem", textAlign: "center", margin: 0 },
    loading: { display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", padding: "2rem 0" },
    spinner: {
        width: "2rem", height: "2rem", border: "3px solid rgba(11,60,93,0.3)",
        borderTopColor: "#0B3C5D", borderRadius: "50%", animation: "spin 0.8s linear infinite",
    }
};
